import { createUseFunction, IPublicRestaurant } from '@localina/core';
import * as React from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ErrorCode, Path } from '../enums';
import { IApplicationStore } from '../interfaces';
import * as actions from '../store/cancelReservation/cancelReservation-actions';
import * as restaurantActions from '../store/restaurant/restaurant-actions';

// properties that provides this context
interface IAppContext {
    restaurantId: string;
    token?: string;
}

interface IProps {
    children?: React.ReactNode;
}

interface IStoreProps {
    restaurant?: IPublicRestaurant;
    isLoading: boolean;
    hasError: boolean;
}

interface IActionProps {
    fetchReservation: typeof actions.fetchReservation;
    requestRestaurantConfiguration: typeof restaurantActions.restaurantConfigurationRequested;
}

const AppContext = React.createContext<null | IAppContext>(null);
AppContext.displayName = 'App Context';

const AppProviderInner: React.FC<IProps & IStoreProps & IActionProps> = (props) => {
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(window.location.search);
    const restaurantId = searchParams.get('id') ?? '';
    const token = searchParams.get('token') || undefined;
    const isMenu = searchParams.has('menu');

    const generateUrl = (errorCode: ErrorCode) => Path.ERROR.replace(':errorCode', `${errorCode}`);

    React.useEffect(() => {
        if (props.hasError) {
            navigate(generateUrl(ErrorCode.RESTAURANT_NOT_FOUND));
        }
    }, [props.hasError]);

    React.useEffect(() => {
        if (restaurantId === '') {
            navigate(generateUrl(ErrorCode.MISSING_QUERY_PARAMS));
        } else if (!props.restaurant) {
            props.requestRestaurantConfiguration(restaurantId);
        }
    }, []);

    React.useEffect(() => {
        if (token && restaurantId) {
            props.fetchReservation(restaurantId, token);
        }
        const path = token ? Path.CANCEL_RESERVATION : isMenu ? Path.RESTAURANT_MENU : Path.START;
        navigate(path, { replace: true });
    }, [token, restaurantId, isMenu]);

    return (
        <AppContext.Provider
            value={{
                restaurantId,
                token,
            }}
        >
            {props.children}
        </AppContext.Provider>
    );
};
const actionsProps: IActionProps = {
    fetchReservation: actions.fetchReservation,
    requestRestaurantConfiguration: restaurantActions.restaurantConfigurationRequested,
};
const mapStateToProps = (state: IApplicationStore): IStoreProps => ({
    restaurant: state.restaurant.configuration,
    isLoading: state.restaurant.isLoading,
    hasError: state.restaurant.hasError,
});
export const AppProvider = connect(mapStateToProps, actionsProps)(AppProviderInner);
export const useApp = createUseFunction<IAppContext>(AppContext);
